import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/pages/layoutChrome";
import CommercialClassificationNotice from "@/components/commercial/CommercialClassificationNotice";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { usePlatformDesigner } from "@/lib/usePlatformDesigner";
import {
  DESIGNER_CL_HUB_OPERATIONAL_SECTION_TITLE,
  getDesignerCompradoresLicenciasHubOperationalLinks,
  getDesignerCompradoresLicenciasHubSections,
} from "@/pages/disenador/designerHubSections";
import { Briefcase, ChevronRight, Info } from "lucide-react";

function CompradoresLicenciasSection({ section, onLink }) {
  return (
    <article
      className="rounded-lg border border-[#0F2D5C]/15 bg-white p-5 shadow-sm space-y-3"
      data-testid={section.testid}
    >
      <div>
        <h2 className="text-base font-semibold text-[#0F2D5C] m-0">{section.title}</h2>
        <p className="text-sm text-slate-600 mt-2 mb-0 leading-relaxed">{section.description}</p>
      </div>
      <ul className="flex flex-col gap-2 m-0 p-0 list-none">
        {(section.links || []).map((link) => (
          <li key={link.testid}>
            <Button
              type="button"
              variant="outline"
              className="w-full justify-between h-auto py-2.5 px-3 text-left font-normal text-sm text-[#0F2D5C] border-[#0F2D5C]/20 hover:bg-[#0F2D5C]/5"
              data-testid={link.testid}
              onClick={() => onLink(link)}
            >
              <span>{link.label}</span>
              <ChevronRight className="w-4 h-4 shrink-0 opacity-60" aria-hidden />
            </Button>
          </li>
        ))}
      </ul>
    </article>
  );
}

function OperationalTransitorySection({ links, onLink }) {
  return (
    <aside
      className="rounded-md border border-dashed border-slate-300 bg-slate-50/80 px-4 py-4 space-y-3"
      data-testid="designer-cl-hub-operacion-transitoria"
    >
      <div>
        <h3 className="text-sm font-semibold text-slate-800 m-0">{DESIGNER_CL_HUB_OPERATIONAL_SECTION_TITLE}</h3>
        <p className="text-xs text-slate-600 mt-2 mb-0 leading-relaxed">
          Mientras la ficha única del comprador/despacho queda integrada, estos accesos se conservan para operar emisión,
          cartera, solicitudes y seguimiento sin perder funcionalidad.
        </p>
      </div>
      <ul className="flex flex-col gap-2 m-0 p-0 list-none">
        {links.map((link) => (
          <li key={link.testid}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full justify-between h-auto py-2 px-3 text-left font-normal text-xs text-slate-700 border-slate-300 bg-white hover:bg-slate-100"
              data-testid={link.testid}
              onClick={() => onLink(link)}
            >
              <span className="flex flex-col items-start gap-0.5 min-w-0">
                <span className="font-medium text-slate-800">{link.label}</span>
                {link.description ? (
                  <span className="text-[11px] text-slate-500 font-normal leading-snug">{link.description}</span>
                ) : null}
              </span>
              <ChevronRight className="w-3.5 h-3.5 shrink-0 opacity-50 ml-2" aria-hidden />
            </Button>
          </li>
        ))}
      </ul>
    </aside>
  );
}

/**
 * Hub Compradores y Licencias — entrada única + operación transitoria (UX/Roles 1D / 1D.1).
 */
export default function DesignerCompradoresLicenciasHub() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPlatformDesigner = usePlatformDesigner();
  const canUse = Boolean(isPlatformDesigner || user?.can_generate_licenses);

  const sections = useMemo(
    () => getDesignerCompradoresLicenciasHubSections({ isPlatformDesigner }),
    [isPlatformDesigner],
  );

  const operationalLinks = useMemo(
    () => getDesignerCompradoresLicenciasHubOperationalLinks({ isPlatformDesigner }),
    [isPlatformDesigner],
  );

  if (user && !canUse) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <p className="text-sm text-slate-600">No tiene acceso al área comercial de compradores y licencias.</p>
      </div>
    );
  }

  const openLink = (link) => {
    navigate(link.path);
  };

  return (
    <div className="min-h-screen">
      <PageHeader
        title="Centro comercial de compradores y licencias"
        subtitle="Administra quién compra o evalúa el software. La licencia pertenece al comprador/despacho, no al usuario individual ni al cliente operativo."
        breadcrumb={["Módulo Maestro Diseñador", "Compradores y Licencias"]}
      />

      <div className="p-8 max-w-4xl mx-auto space-y-6" data-testid="designer-compradores-licencias-hub">
        <div
          className="rounded-md border border-[#0F2D5C]/15 bg-[#0F2D5C]/5 px-4 py-3 text-sm text-slate-700 flex gap-3"
          data-testid="designer-cl-hub-banner-entry-unica"
        >
          <Briefcase className="w-5 h-5 shrink-0 text-[#0F2D5C]" aria-hidden />
          <p className="m-0 leading-relaxed">
            Flujo simplificado: primero se registra o selecciona el comprador/despacho en el catálogo. La ficha única
            integrará licencia y módulos; mientras tanto, usa la operación transitoria de abajo.
          </p>
        </div>

        <CommercialClassificationNotice variant="buyerCatalog" />

        <section
          aria-label="Entrada principal compradores y licencias"
          data-testid="designer-cl-hub-sections"
          className="grid grid-cols-1 gap-5"
        >
          {sections.map((section) => (
            <CompradoresLicenciasSection key={section.id} section={section} onLink={openLink} />
          ))}
        </section>

        <OperationalTransitorySection links={operationalLinks} onLink={openLink} />

        <div
          className="rounded-md border border-sky-200 bg-sky-50/90 px-4 py-3 text-sm text-slate-800 flex gap-3"
          data-testid="designer-cl-hub-deduplication-notice"
          role="note"
        >
          <Info className="w-5 h-5 shrink-0 text-sky-700 mt-0.5" aria-hidden />
          <p className="m-0 leading-relaxed">
            La entrada principal sigue siendo el catálogo de compradores. Los accesos transitorios evitan perder emisión,
            cartera y solicitudes hasta integrar todo en la ficha única del comprador/despacho.
          </p>
        </div>

        <aside
          className="rounded-md border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-700"
          data-testid="designer-cl-hub-pending-unification"
        >
          <h3 className="text-sm font-semibold text-slate-900 m-0">Pendiente de unificación</h3>
          <p className="mt-2 mb-0 leading-relaxed text-slate-600">
            Organizaciones SaaS, licencias SaaS, cartera distribuidor y solicitudes legales siguen disponibles como rutas
            internas/operativas. El objetivo siguiente es integrarlas en una ficha única por comprador/despacho.
          </p>
        </aside>
      </div>
    </div>
  );
}
